import { constant } from "fp-ts/lib/function";
import useFormat from "hooks/useFormat";
import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { StackedBarAuto } from "../charts-motion";
import { withFigureIO } from "../components/figure";
import { useReadScrolly } from "../components/scrolly";
import { useConfig } from "../config";
import { metadata } from "../data/data_access_fig_OVERAGE";
import { datasets } from "../data/data_access_fig_OVERAGE_datasets";
import { countryIdIso, getCountryMeta, getCountryName, mkGemDataDecoder, useFigureControlItems, useSingleSelectState } from "../domain";
import { useMedia } from "../hooks";
import * as M from "../materials";
import { mkScrollyColorPalette } from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { An, Ar, io, O, Ord, pipe, tuple } from "../prelude";
export * from "../data/data_access_fig_OVERAGE";
const DEFAULT_COUNTRY = "KHM";
const attendKeyOrd = {
    "attend.preschool": 0,
    "attend.primary": 1,
    "attend.secondary": 2,
    "attend.higher": 3,
    "no.attend": 4
};
const AttendKey = io.keyof(attendKeyOrd);
export const Data = mkGemDataDecoder([
    "iso3c",
    "year"
], {
    age: io.number,
    ind_id: AttendKey,
    value: io.number
});
const getAttendKeyIndex = (k)=>attendKeyOrd[k];
const attendKeyLookup = {
    "attend.preschool": defineMessage({
        id: "fig.access.OVERAGE.attend.preschool",
        defaultMessage: "Pre-primary"
    }),
    "attend.primary": defineMessage({
        id: "fig.access.OVERAGE.attend.primary",
        defaultMessage: "Primary"
    }),
    "attend.secondary": defineMessage({
        id: "fig.access.OVERAGE.attend.secondary",
        defaultMessage: "Secondary"
    }),
    "attend.higher": defineMessage({
        id: "fig.access.OVERAGE.attend.higher",
        defaultMessage: "Higher"
    }),
    "no.attend": defineMessage({
        id: "fig.access.OVERAGE.no.attend",
        defaultMessage: "Out of school"
    })
};
export const Chart = React.memo(({ data })=>{
    const screenMDown = !useMedia(breakpoints.m);
    const intl = useIntl();
    const env = useConfig();
    const [selectedCountry, actions] = useSingleSelectState("country");
    const { activeSection, sections } = useReadScrolly();
    const scrollyCountry = React.useMemo(()=>pipe(activeSection, O.chain((strId)=>getCountryMeta(env.countries, countryIdIso.wrap(strId))), O.map((meta)=>meta.iso3c), O.getOrElse(()=>countryIdIso.wrap(DEFAULT_COUNTRY))), [
        env.countries,
        activeSection
    ]);
    const sortedData = React.useMemo(()=>{
        const ordDimensions = Ord.getTupleOrd(Ord.ordString, Ord.ordNumber, Ord.ordNumber);
        const ord = Ord.contramap((x)=>tuple(countryIdIso.unwrap(x.iso3c), x.age, getAttendKeyIndex(x.ind_id)))(ordDimensions);
        return Ar.sort(ord)(data);
    }, [
        data
    ]);
    const countryControlItems = useFigureControlItems(sortedData, (x)=>countryIdIso.unwrap(x.iso3c), (x)=>getCountryName(env.countries, x.iso3c));
    React.useEffect(()=>{
        actions.setSelectionControl("country", {
            type: "SingleSelect",
            selected: O.some(DEFAULT_COUNTRY)
        }, countryControlItems);
    }, [
        actions,
        countryControlItems
    ]);
    React.useEffect(()=>{
        if (O.isSome(activeSection)) {
            actions.updateSelectionControl("country", O.some(countryIdIso.unwrap(scrollyCountry)));
        }
    }, [
        actions,
        activeSection,
        scrollyCountry
    ]);
    const chartData = React.useMemo(()=>{
        return sortedData.filter(({ iso3c })=>pipe(selectedCountry, O.fold(constant(false), (x)=>x === countryIdIso.unwrap(iso3c))));
    }, [
        sortedData,
        selectedCountry
    ]);
    const annotations = React.useMemo(()=>{
        return pipe(selectedCountry, O.chain((strId)=>getCountryMeta(env.countries, countryIdIso.wrap(strId))), O.map((meta)=>({
                label: intl.formatMessage(defineMessage({
                    id: "fig.access.OVERAGE.level_ages_label",
                    defaultMessage: "Theoretical age range for:"
                })),
                items: [
                    {
                        layer: "attend.primary",
                        startStack: `${meta.level_ages[0]}`,
                        endStack: `${meta.level_ages[1]}`
                    },
                    {
                        layer: "attend.secondary",
                        startStack: `${meta.level_ages[1]}`,
                        endStack: `${meta.level_ages[2]}`
                    }
                ]
            })), O.toUndefined);
    }, [
        intl,
        env.countries,
        selectedCountry
    ]);
    const currentPalette = React.useMemo(()=>{
        const countryKeys = sections.size > 0 ? Array.from(sections.values()) : [];
        return mkScrollyColorPalette(countryKeys, selectedCountry);
    }, [
        sections,
        selectedCountry
    ]);
    const getStack = React.useCallback((x)=>`${x.age}`, []);
    const getLayer = React.useCallback((x)=>`${x.ind_id}`, []);
    const getLayerLabel = React.useCallback((x)=>intl.formatMessage(attendKeyLookup[x]) || x, [
        intl
    ]);
    const getValue = React.useCallback((x)=>x.value, []);
    const formatX = React.useCallback((stack, i)=>i === 0 ? screenMDown ? `${stack}y` : `${stack} years` : stack, [
        screenMDown
    ]);
    const formatY = useFormat(".0%");
    const getColorPalette = React.useCallback((count)=>M.fromCount(currentPalette, count - 1).concat(M.grayscalePalette[3]), [
        currentPalette
    ]);
    return pipe(An.fromArray(chartData), O.map((xs)=><StackedBarAuto key={"linter-map"} height={screenMDown ? M.chartHeight.s : M.chartHeight.m} data={xs} getStack={getStack} getLayer={getLayer} getLayerLabel={getLayerLabel} getValue={getValue} formatX={formatX} formatY={formatY} colorPalette={getColorPalette} showTooltip annotations={annotations}/>), O.toNullable);
});
export default withFigureIO({
    url: new URL("../data/data_access_fig_OVERAGE.json", import.meta.url).href,
    csv: new URL("../data/data_access_fig_OVERAGE.zip", import.meta.url).href,
    xlsx: new URL("../data/data_access_fig_OVERAGE.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    datasets
});
